import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Checkbox, FormControlLabel, FormGroup, Grid2, Stack, TextField, Typography, useTheme } from "@mui/material";
import {
  updateAdvisiorReview,
  createAdvisiorReview,
  utilitiesAdvisorReview as utilitiesAdvisorReviewApi,
  detailAdvisorReview,
  sanitizeLabel
} from "../../../../apis/apiAdvisorReview";
import { ChipInfo } from "../../../MainComponents/ChipInfo/ChipInfo";

import hexAr from '../../../../assets/image/flags/hx-ar.svg';
import hexBo from '../../../../assets/image/flags/hx-bo.svg';
import hexBr from '../../../../assets/image/flags/hx-br.svg';
import hexCa from '../../../../assets/image/flags/hx-ca.svg';
import hexCl from '../../../../assets/image/flags/hx-cl.svg';
import hexCn from '../../../../assets/image/flags/hx-cn.svg';
import hexCo from '../../../../assets/image/flags/hx-co.svg';
import hexCr from '../../../../assets/image/flags/hx-cr.svg';
import hexDe from '../../../../assets/image/flags/hx-de.svg';
import hexEc from '../../../../assets/image/flags/hx-ec.svg';
import hexMx from '../../../../assets/image/flags/hx-mx.svg';
import hexSv from '../../../../assets/image/flags/hx-sv.svg';
import hexUs from '../../../../assets/image/flags/hx-us.svg';

// import hexMx from '../../../../assets/image/flags/hx-mx.svg';
// import hexUs from '../../../../assets/image/flags/us.png';
// import hexHo from '../../../../assets/image/flags/ho.png';
import Icon from "../../../Icon/Icon";
import { EGButton } from "../../../MainComponents/Button/EGButton/EGButton";
import { EGBasicSelect } from "../../../MainComponents/Select/EGBasicSelect";

/**
 * Funcion para obtener los textos por valor en la lista de utils de la revision del asesor
 * @param {Array} utilList Lista de los utilities que donde se va a obtener la informacion
 * @param {*} value El valor actual que tiene la revision del asesor
 * @param {string} valueKey La llave de el valor que hay que buscar
 * @param {string} labelKey La llave del label que queremos mostrar
 * @returns {string} Label del valor actual
 */
const getLabelFromUtilities = (utilList, value, valueKey, labelKey) => {
  if (!Array.isArray(utilList)) return undefined;
  const found = utilList.find((item) => item[valueKey] === value);
  return found ? found[labelKey] : undefined;
}

/**
 * Mapeos de respaldo para país/idioma que aún no están en el catálogo del
 * backend (endpoint /advisor/review/utilities/). Evita mostrar "Indefinido"
 * mientras el catálogo se actualiza. Eliminar cuando el back los incluya.
 */
const FALLBACK_LANGUAGES = {
  zh: "Chinese", // se pasa por sanitizeLanguage -> "chinese" -> t("AdvisorReview.chinese")
};
const FALLBACK_COUNTRIES = {
  CHN: { es: "China", en: "China", pt: "China" },
};

const CheckboxReview = ({ item, handleQuestions, target, disabled }) => {
  const theme = useTheme();
  return (
    <FormGroup key={item.value}>
      <FormControlLabel 
        label={item.question} 
        sx={{
          '& .MuiTypography-root': { fontSize: 16, fontWeight: 600, color: theme.palette.secondary.main },
        }}
        control={ 
          <Checkbox
            sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }} 
            checked={item.is_checked}
            onChange={(e)=>handleQuestions(target, item.value, e.target.checked)}
            disabled={disabled}
          />
        } 
      />
    </FormGroup>
  )
}

export const AdvisorReview = ({ report_id, edit = false }) => { // Edicion

  const { i18n, t } = useTranslation("information");
  const currentLanguage = i18n.language;
  const theme = useTheme();

  const [utilitiesAdvisorReview, setUtilitiesAdvisorReview] = useState(null);
  const [advisorReview, setAdvisorReview] = useState(null);
  const [questionsOfParams, setQuestionsOfParams] = useState(
    startQuestion(
      utilitiesAdvisorReview?.questions_of_params,
      advisorReview?.questions_of_params
    )
  );
  const [questionsOfExtraDetails, setQuestionsOfExtraDetails] = useState(
    startQuestion(
      utilitiesAdvisorReview?.questions_of_extra_details,
      advisorReview?.questions_of_extra_details
    )
  );
  const [country, setCountry] = useState(advisorReview?.country || null);
  const [language, setLanguage] = useState(
    utilitiesAdvisorReview?.list_languages?.find(
      (item) => item.code === advisorReview?.language
    )?.value || null
  );
  const [severity, setSeverity] = useState(advisorReview?.severity || null);
  const [event, setEvent] = useState(advisorReview?.event || null);
  const [call_tone, setCall_tone] = useState(advisorReview?.call_tone || null);
  const [comment, setComment] = useState(advisorReview?.comment || "");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    
    detailAdvisorReview(report_id)
    .then((resp) => setAdvisorReview(resp.review ?? null))
    .catch((err) => console.log(err))

    utilitiesAdvisorReviewApi()
    .then((resp)=>setUtilitiesAdvisorReview(resp.data ?? null))
    .catch(console.log)

  }, [report_id, edit]);

  useEffect(() => { // Edit
    if (!edit) return;
    if (!advisorReview || !utilitiesAdvisorReview) return;
    setQuestionsOfParams(
      startQuestion(
        utilitiesAdvisorReview?.questions_of_params,
        advisorReview?.questions_of_params
      )
    );
    setQuestionsOfExtraDetails(
      startQuestion(
        utilitiesAdvisorReview?.questions_of_extra_details,
        advisorReview?.questions_of_extra_details
      )
    );
    setCountry(advisorReview?.country || null);
    setLanguage(
      utilitiesAdvisorReview?.list_languages?.find(
        (item) => item.code === advisorReview?.language
      )?.value || null
    );
    setSeverity(advisorReview?.severity || null);
    setEvent(advisorReview?.event || null);
    setCall_tone(advisorReview?.call_tone || null);
    setComment(advisorReview?.comment || "");
  }, [advisorReview, utilitiesAdvisorReview]);

  useEffect(() => { // View
    if (edit) return;
    if (!advisorReview || !utilitiesAdvisorReview) return;
    setQuestionsOfParams(
      startQuestion(
        utilitiesAdvisorReview?.questions_of_params,
        advisorReview?.questions_of_params
      )
    );
    setQuestionsOfExtraDetails(
      startQuestion(
        utilitiesAdvisorReview?.questions_of_extra_details,
        advisorReview?.questions_of_extra_details
      )
    );
    
    const languageName =
      getLabelFromUtilities(utilitiesAdvisorReview.list_languages, advisorReview?.language, 'code', 'name')
      ?? FALLBACK_LANGUAGES[advisorReview?.language];
    setLanguage(t(`AdvisorReview.${sanitizeLanguage(languageName)}`));
    setSeverity(sanitizeSeverity(advisorReview?.severity 
      ? t(`AdvisorReview.${advisorReview.severity}`) 
      : t("AdvisorReview.undefined")));
    setCall_tone(advisorReview?.call_tone ?? t("AdvisorReview.undefined"));
    setComment(advisorReview?.comment ?? t("AdvisorReview.without_comments"));
  }, [advisorReview, utilitiesAdvisorReview]);

  useEffect(() => {
    const countryObject = utilitiesAdvisorReview?.list_countries?.find(item => item.value === advisorReview?.country);
    const fallbackCountry = FALLBACK_COUNTRIES[advisorReview?.country];
    const baseLang = currentLanguage?.slice(0, 2);
    const translatedCountry =
      countryObject?.locale?.[currentLanguage] ||
      countryObject?.locales?.[currentLanguage] ||
      fallbackCountry?.[currentLanguage] ||
      fallbackCountry?.[baseLang] ||
      fallbackCountry?.en;
    setCountry(translatedCountry ?? t("AdvisorReview.undefined"));
  }, [advisorReview, utilitiesAdvisorReview]);

  // Función para cambiar los labels de idioma y severidad
  function sanitizeLanguage(language) {
    if (!language) return "undefined";
    if (language.toLowerCase().includes("spanish; castilian")) {
      return "spanish";
    }
    return language.toLowerCase();
  }

  function sanitizeSeverity(severity) {
    if (severity) {
    return severity.charAt(0).toUpperCase() + severity.slice(1).toLowerCase();
    }
  }

  useEffect(() => {
    isValid();
  }, [country, language, severity, event, call_tone, comment]);
  
  const list_severity_formated = utilitiesAdvisorReview?.list_severity?.map((item) => ({ label: item, value: item,})) || [];
  const list_events_formated = utilitiesAdvisorReview?.list_events?.map((item) => ({ label: item, value: item })) || [];
  const list_call_tone_formated = utilitiesAdvisorReview?.list_call_tone?.map((item) => ({ label: item, value: item })) || [];

  function startQuestion(util, answers) {
        // Sanitiza los labels (temporalmente)
    const arrQuestions = Array.isArray(util) ? util.map((item) => ({
      ...item,
      question: sanitizeLabel(item.question),
    })) : [];

    for (const key in answers) {
      const idxAnswer = arrQuestions.findIndex((item) => item.value === key);
      if (idxAnswer >= 0) {
        arrQuestions[idxAnswer].is_checked = answers[key].is_checked;
      }
    }
    return arrQuestions;
  }

  function handleChange(id, value) {
    if (id === "country") {
      setCountry(value);
    }
    if (id === "language") {
      setLanguage(value);
    }
    if (id === "severity") {
      setSeverity(value);
    }
    if (id === "event") {
      setEvent(value);
    }
    if (id === "call_tone") {
      setCall_tone(value);
    }
    if (id === "comment") {
      setComment(value);
    }
  };

  function handleQuestions(target, value, checked) {
    if (target === "params") {
      setQuestionsOfParams(
        questionsOfParams.map((item) =>
          item.value === value ? { ...item, is_checked: checked } : item
        )
      );
    }
    if (target === "extra") {
      setQuestionsOfExtraDetails(
        questionsOfExtraDetails.map((item) =>
          item.value === value ? { ...item, is_checked: checked } : item
        )
      );
    }
  };

  function isValid() {
    const newErrors = {};
    if (country === null) {
      Array.isArray(newErrors.country)
        ? newErrors.country.push(t("AdvisorReview.field_must_be_complete"))
        : (newErrors.country = [t("AdvisorReview.field_must_be_complete")]);
    }
    if (language === null) {
      Array.isArray(newErrors.language)
        ? newErrors.language.push(t("AdvisorReview.field_must_be_complete"))
        : (newErrors.language = t("AdvisorReview.field_must_be_complete"));
    }
    if (severity === null) {
      Array.isArray(newErrors.severity)
        ? newErrors.severity.push(t("AdvisorReview.field_must_be_complete"))
        : (newErrors.severity = [t("AdvisorReview.field_must_be_complete")]);
    }
    if (event === null) {
      Array.isArray(newErrors.event)
        ? newErrors.event.push(t("AdvisorReview.field_must_be_complete"))
        : (newErrors.event = [t("AdvisorReview.field_must_be_complete")]);
    }
    if (call_tone === null) {
      Array.isArray(newErrors.call_tone)
        ? newErrors.call_tone.push(t("AdvisorReview.field_must_be_complete"))
        : (newErrors.call_tone = [t("AdvisorReview.field_must_be_complete")]);
    }
    if (comment.trim() === "") {
      Array.isArray(newErrors.comment)
        ? newErrors.comment.push(t("AdvisorReview.field_must_be_complete"))
        : (newErrors.comment = [t("AdvisorReview.field_must_be_complete")]);
    }
    setErrors(newErrors);
    return Object.keys(errors).length === 0 ? true : false;
  };

  function returnFlag(flag) {
    const aviableFlags = [ "ARG", "BOL", "BRA", "CAN", "CHL", "CHN", "COL", "CRI", "ECU", "MEX", "SLV", "USA" ];
    if (!flag || !aviableFlags.includes(flag)) {
      return (
        <div style={{ display: 'grid', placeContent: 'center' }}>
          <Icon name='outline_location' size={16} />
        </div>
      )
    }

    if (flag === "ARG") return <img src={hexAr} />;
    if (flag === "BOL") return <img src={hexBo} />;
    if (flag === "BRA") return <img src={hexBr} />;
    if (flag === "CAN") return <img src={hexCa} />;
    if (flag === "CHL") return <img src={hexCl} />;
    if (flag === "CHN") return <img src={hexCn} />;
    if (flag === "COL") return <img src={hexCo} />;
    if (flag === "CRI") return <img src={hexCr} />;
    if (flag === "ECU") return <img src={hexEc} />;
    if (flag === "MEX") return <img src={hexMx} />;
    if (flag === "SLV") return <img src={hexSv} />;
    if (flag === "USA") return <img src={hexUs} />;

  }

  async function saveAdvisorReview() {
    const valid = isValid();
    if (!valid) return;
    const newQuestionsOfParams = {};
    const newQuestionsOfExtraDetails = {};
    questionsOfParams.forEach((item) => {
      newQuestionsOfParams[item.value] = item.is_checked;
    });
    questionsOfExtraDetails.forEach((item) => {
      newQuestionsOfExtraDetails[item.value] = item.is_checked;
    });
    const objToSave = {
      report_id,
      ...(advisorReview && { review_id: advisorReview.id }),
      country,
      language,
      severity,
      event: "1 evento",
      call_tone,
      comment,
      questions_of_params: newQuestionsOfParams,
      questions_of_extra_details: newQuestionsOfExtraDetails,
    };
    try {
      if (advisorReview) {
        await updateAdvisiorReview(objToSave);

      } else {
        await createAdvisiorReview(objToSave);
      }
    } catch (error) {
      
    }
  };
  
  if (edit && !utilitiesAdvisorReview) {
    return <></>
  }
  if (!edit && !advisorReview) {
    return (
      <Typography variant="h6">
        {t("AdvisorReview.no_comments")}
      </Typography>
    )
  }

  return (
    <>
      { // Chips
        !edit &&
        <Stack
          spacing={2}
          direction="row"
          flexWrap={"wrap"}
          sx={{ padding: 1 }}
        >
          <ChipInfo 
            label={<span>{t("AdvisorReview.received_in")}: <b>{country}</b></span>}
            icon={returnFlag(advisorReview?.country) } 
            variant='outlined' 
            color={theme.palette.blueGray200.main} 
            maxWidth={200}
          />

          <ChipInfo 
            label={<span>{t("AdvisorReview.language")}: <b>{language}</b></span>}
            icon={
              <div style={{ display: 'grid', placeContent: 'center' }}>
                <Icon name='outline_translate' size={16} />
              </div>
            } 
            variant='outlined' 
            color={'success'} 
            maxWidth={200}
          />

          <ChipInfo 
            label={<span>{t("AdvisorReview.severity")}: <b>{severity}</b></span>}
            icon={
              <div style={{ display: 'grid', placeContent: 'center' }}>
                <Icon name='bold_flash_2' size={16} />
              </div>
            }
            variant='outlined'
            color='warning'
            maxWidth={200}
          />

        </Stack>
      }

      { // Selects
        edit &&
        <Grid2
          spacing={2}
          container
          sx={{ padding: 1 }}
        >

          <Grid2 size={{ xs: 12, md: 6 }}>
            <EGBasicSelect
              label={t("AdvisorReview.country")}
              value={country}
              options={utilitiesAdvisorReview?.list_countries || []}
              onChange={(e) => handleChange('country', e.target.value)}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <EGBasicSelect
              label={t("AdvisorReview.language")}
              value={language}
              options={utilitiesAdvisorReview?.list_languages || []}
              optionLabel="name"
              onChange={(e) => handleChange("language", e.target.value)}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <EGBasicSelect
              label={t("AdvisorReview.severity")}
              value={severity}
              options={list_severity_formated || []}
              onChange={(e) =>handleChange("severity", e.target.value)}
            />
          </Grid2>

          {/* <Grid2 size={{ xs: 12, md: 6 }}>
            <EGBasicSelect
              label={t("AdvisorReview.event")}
              value={event}
              options={list_events_formated || []}
              onChange={(e) => handleChange("event", e.target.value)}
            />
          </Grid2> */}

          <Grid2 size={{ xs: 12, md: 6 }}>
            <EGBasicSelect
              label={t("AdvisorReview.tone")}
              value={call_tone}
              options={list_call_tone_formated || []}
              onChange={(e) => handleChange("call_tone", e.target.value)}
            />
          </Grid2>

        </Grid2>
      }
      
      {/* Checkboxes */}
      <Stack
        spacing={.8}
        sx={{
          justifyContent: "space-between",
          alignItems: "flex-start",
          padding: 1
        }}
      >

        {Array.isArray(questionsOfParams) &&
          questionsOfParams.map((item, idx) => (
            <CheckboxReview
              key={`qp-${idx}`} 
              item={{...item, question: t(`questions_of_params.${item.question}`)}}
              handleQuestions={handleQuestions}
              target="params"
              disabled={!edit}
            />
          ))}

        {Array.isArray(questionsOfExtraDetails) &&
          questionsOfExtraDetails.map((item, idx) => (
            <CheckboxReview
              key={`qe-${idx}`} 
              item={{...item, question: t(`questions_of_extra_details.${item.question}`)}}
              handleQuestions={handleQuestions}
              target="extra"
              disabled={!edit}
            />
        ))}

      </Stack>

      { // Textfield comment
        !edit && 
        <Box padding={1}>

          <Typography variant="h4" sx={{ fontSize: 14, color: theme.palette.primary.main }}>
            {t("AdvisorReview.tone_percived")}
          </Typography>

          <Typography variant="body1" component="p" sx={{ fontSize: 16, borderBottom: `1px solid ${theme.palette.blueGray200.main}` }} >
            {call_tone}
          </Typography>

        </Box>
      }

      { // Tono
        !edit &&
        <Box padding={1}>

          <Typography variant="h4" sx={{ fontSize: 14, color: theme.palette.primary.main }}>
            {t("AdvisorReview.comments")}
          </Typography>

          <Typography variant="body1" component="p" sx={{ fontSize: 16, borderBottom: `1px solid ${theme.palette.blueGray200.main}` }} >
            {comment}
          </Typography>

        </Box>
      }

      { // comment
        edit &&
        <Box>
          <TextField
            label={t("AdvisorReview.comments")}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            className="w-100 mt-3"
            multiline
          />
        </Box>
      }

      { // Button save
        edit &&
        <EGButton 
          label={t("information.save")}
          variant="outlined"
          onClick={saveAdvisorReview}
          sx={{ marginTop: 1 }}
        />
      }
    </>
  );
};